import { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Plus, Pencil, Trash2, Target, Boxes } from "lucide-react";
import { toast } from "sonner";
import { StockDialog } from "../../components/StockDialog";
import { stockTone, fmtQty } from "../../lib/stock";

const empty = {
  name: "",
  retail_price: "",
  wholesale_price: "",
  sku: "",
  unit: "",
  active: true,
  monthly_target_qty: 0,
  low_stock_threshold: 10,
};

const fmtNum = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 }).format(n || 0);

export default function Products() {
  const [items, setItems] = useState([]);
  const [progress, setProgress] = useState({ rows: [], month: "" });
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [targetTarget, setTargetTarget] = useState(null);
  const [targetValue, setTargetValue] = useState("");
  const [targetSaving, setTargetSaving] = useState(false);
  const [stockProduct, setStockProduct] = useState(null);
  const [lowOnly, setLowOnly] = useState(false);

  const load = async () => {
    const [pRes, prRes] = await Promise.all([
      api.get("/products"),
      api.get("/products/targets/progress").catch(() => ({ data: { rows: [], month: "" } })),
    ]);
    setItems(pRes.data);
    setProgress(prRes.data);
  };
  useEffect(() => {
    load();
  }, []);

  const progressMap = new Map(progress.rows.map((r) => [r.product_id, r]));

  const isLow = (p) => Number(p.stock_qty || 0) <= Number(p.low_stock_threshold ?? 10);
  const lowCount = items.filter((p) => p.active && isLow(p)).length;
  const filtered = items.filter(
    (p) => p.name.toLowerCase().includes(q.toLowerCase()) && (!lowOnly || isLow(p)),
  );

  const openNew = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (p) => {
    setForm({
      name: p.name,
      retail_price: p.retail_price,
      wholesale_price: p.wholesale_price,
      sku: p.sku || "",
      unit: p.unit || "",
      active: p.active,
      monthly_target_qty: p.monthly_target_qty || 0,
      low_stock_threshold: p.low_stock_threshold ?? 10,
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        name: form.name,
        retail_price: Number(form.retail_price),
        wholesale_price: Number(form.wholesale_price),
        sku: form.sku || null,
        unit: form.unit || null,
        active: !!form.active,
        monthly_target_qty: Number(form.monthly_target_qty) || 0,
        low_stock_threshold: Number(form.low_stock_threshold) || 0,
      };
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const openTargetDialog = (p) => {
    setTargetTarget(p);
    setTargetValue(String(p.monthly_target_qty || 0));
  };

  const saveTarget = async () => {
    if (!targetTarget) return;
    const v = Number(targetValue);
    if (Number.isNaN(v) || v < 0) return toast.error("Enter a valid quantity");
    setTargetSaving(true);
    try {
      await api.put(`/products/${targetTarget.id}/target`, {
        monthly_target_qty: v,
      });
      toast.success(`Allotment saved: ${fmtNum(v)} ${targetTarget.unit || "units"}`);
      setTargetTarget(null);
      load();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setTargetSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-products-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[color:var(--muted)]">Catalog</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Products</h1>
          <p className="text-[color:var(--muted)] mt-1">
            Dual pricing & monthly allotment{progress.month ? ` · ${progress.month}` : ""}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setLowOnly((v) => !v)}
            className={`h-10 px-3 rounded-lg text-sm font-semibold border transition-colors ${lowOnly ? "bg-[color:var(--primary)] text-white border-[color:var(--primary)]" : "bg-white border-[color:var(--border)] text-[color:var(--ink)] hover:bg-[color:var(--surface-2)]"}`}
            data-testid="products-low-stock-filter"
          >
            Low stock ({lowCount})
          </button>
          <Input
            placeholder="Search products…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-56"
            data-testid="products-search-input"
          />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="add-product-button">
                <Plus className="h-4 w-4 mr-1" /> Add product
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="product-dialog">
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit product" : "New product"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Retail price</Label>
                    <Input type="number" step="0.01" value={form.retail_price} onChange={(e) => setForm({ ...form, retail_price: e.target.value })} data-testid="product-retail-input" />
                  </div>
                  <div>
                    <Label>Wholesale price</Label>
                    <Input type="number" step="0.01" value={form.wholesale_price} onChange={(e) => setForm({ ...form, wholesale_price: e.target.value })} data-testid="product-wholesale-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>SKU (optional)</Label>
                    <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
                  </div>
                  <div>
                    <Label>Unit (kg, pack, ...)</Label>
                    <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="e.g. pack" data-testid="product-unit-input" />
                  </div>
                </div>
                <div>
                  <Label>Monthly allotment (qty)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.monthly_target_qty}
                    onChange={(e) => setForm({ ...form, monthly_target_qty: e.target.value })}
                    data-testid="product-target-input"
                  />
                  <div className="text-xs text-[color:var(--muted)] mt-1">
                    Target sales quantity for the current month. Leave 0 to skip tracking.
                  </div>
                </div>
                <div>
                  <Label>Low-stock alert level (qty)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={form.low_stock_threshold}
                    onChange={(e) => setForm({ ...form, low_stock_threshold: e.target.value })}
                    data-testid="product-low-stock-input"
                  />
                  <div className="text-xs text-[color:var(--muted)] mt-1">
                    Product shows as low stock when available qty is at or below this level.
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <Label>Active</Label>
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="save-product-button">
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="rounded-xl border border-[color:var(--border)] bg-white ink-shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[color:var(--surface-2)]">
              <tr className="text-left text-[color:var(--muted)] uppercase text-xs">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3 text-right">Retail ₹</th>
                <th className="px-4 py-3 text-right">Wholesale ₹</th>
                <th className="px-4 py-3 text-right">Stock</th>
                <th className="px-4 py-3 text-right">Allotment</th>
                <th className="px-4 py-3">Sold this month</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const pr = progressMap.get(p.id) || { sold: 0, pct: 0, target: p.monthly_target_qty || 0 };
                const tone =
                  pr.pct >= 100
                    ? "bg-[color:var(--moss)]"
                    : pr.pct >= 60
                    ? "bg-[color:var(--mustard)]"
                    : "bg-[color:var(--primary)]";
                const hasTarget = (pr.target || 0) > 0;
                return (
                  <tr key={p.id} className="border-t border-[color:var(--border)]" data-testid={`product-row-${p.id}`}>
                    <td className="px-4 py-3 text-[color:var(--muted)]">{i + 1}</td>
                    <td className="px-4 py-3 font-medium font-tamil">
                      <div>{p.name}</div>
                      {p.sku && <div className="text-[10px] text-[color:var(--muted)]">{p.sku}</div>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{p.retail_price.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{p.wholesale_price.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <button
                        onClick={() => setStockProduct(p)}
                        className={`text-xs px-2 py-1 rounded-full font-semibold inline-flex items-center gap-1 hover:opacity-80 ${stockTone(p)}`}
                        data-testid={`product-stock-${p.id}`}
                      >
                        <Boxes className="h-3 w-3" />
                        {fmtQty(p.stock_qty)} {p.unit || ""}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <button
                        onClick={() => openTargetDialog(p)}
                        className={`text-xs px-2 py-1 rounded-full font-semibold ${
                          hasTarget
                            ? "bg-[color:var(--ink)]/5 text-[color:var(--ink)] hover:bg-[color:var(--ink)]/10"
                            : "bg-[color:var(--surface-2)] text-[color:var(--muted)] hover:bg-[color:var(--surface-2)]"
                        }`}
                        data-testid={`set-product-target-${p.id}`}
                      >
                        <Target className="h-3 w-3 inline -mt-0.5 mr-1" />
                        {hasTarget ? `${fmtNum(pr.target)} ${p.unit || ""}` : "Set"}
                      </button>
                    </td>
                    <td className="px-4 py-3 min-w-[180px]">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="tabular-nums font-semibold">
                          {fmtNum(pr.sold)} {p.unit || ""}
                        </span>
                        {hasTarget ? (
                          <span className="tabular-nums text-[color:var(--muted)]">{pr.pct}%</span>
                        ) : (
                          <span className="text-[color:var(--muted)]">—</span>
                        )}
                      </div>
                      {hasTarget && (
                        <div className="h-1.5 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${tone}`}
                            style={{ width: `${Math.min(100, pr.pct)}%` }}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          p.active ? "bg-[color:var(--moss)]/10 text-[color:var(--moss)]" : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {p.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(p)} className="p-1 text-[color:var(--muted)] hover:text-[color:var(--ink)]" data-testid={`edit-product-${p.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => remove(p.id)} className="p-1 ml-1 text-[color:var(--muted)] hover:text-[color:var(--primary)]" data-testid={`delete-product-${p.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="9" className="py-10 text-center text-[color:var(--muted)]">
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <StockDialog product={stockProduct} onClose={() => setStockProduct(null)} onSaved={load} />

      <Dialog open={!!targetTarget} onOpenChange={(o) => !o && setTargetTarget(null)}>
        <DialogContent data-testid="product-target-dialog">
          <DialogHeader>
            <DialogTitle>
              Monthly allotment{targetTarget ? ` · ${targetTarget.name}` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-[color:var(--muted)]">
              Set a target sales quantity for the current calendar month. Enter 0 to disable
              tracking for this commodity.
            </div>
            <div>
              <Label>Target quantity ({targetTarget?.unit || "units"})</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                className="font-mono h-12"
                data-testid="product-target-value-input"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTargetTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={saveTarget}
                disabled={targetSaving}
                className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white"
                data-testid="product-target-save"
              >
                {targetSaving ? "Saving…" : "Save allotment"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
